import React, { useState } from 'react';
import { useFormikContext, getIn } from 'formik';
import { useParams } from 'react-router-dom';

import { LESSON_TYPES } from 'constants/common';

import * as api from 'api/methods';

import { showToast } from 'helpers/showToast';
import { getTranslation } from 'helpers/getTranslation';

import GeneralSection from './GeneralSection';

const GeneralSectionContainer = ({
  namePrefix,
  activeLocale,
  lessonType = LESSON_TYPES.FIGMA_LESSON,
  onTaskTranslated,
  ...restProps
}: {
  namePrefix: any;
  activeLocale: string;
  lessonType?: string;
  onTaskTranslated?: () => Promise<void>;
} & Record<string, any>) => {
  const { lessonId } = useParams();
  const [translateLoading, setTranslateLoading] = useState(false);

  const { values, setFieldValue } = useFormikContext<any>();

  const handleTranslateClick = async () => {
    const targetLocale = activeLocale;

    setTranslateLoading(true);

    try {
      const task = getIn(values, namePrefix);

      if (!task || namePrefix === 'initialState') {
        return;
      }

      const taskIndex = values.tasks.findIndex((t: any) => t.id === task.id);
      const sourceText = getIn(values, `${namePrefix}.text`);

      if (taskIndex < 0) {
        throw new Error('Task index not found');
      }

      if (sourceText == null || sourceText === '') {
        throw new Error('Current task text is empty');
      }

      const mergedLesson = await api.translateTask(lessonId as string, {
        text: sourceText,
        taskIndex,
        language: targetLocale,
      });

      const translatedText = mergedLesson?.tasks?.[taskIndex]?.text;

      if (translatedText == null) {
        throw new Error('Translation response is empty');
      }

      setFieldValue(`${namePrefix}.text`, translatedText);

      if (onTaskTranslated) {
        await onTaskTranslated();
      }

      showToast({
        data: {
          variant: 'success',
          title: getTranslation('common.toast.lesson_saved.title'),
          text: getTranslation(
            'figma_lesson_page.main_settings.general_section.translate_success',
          ),
        },
      });
    } catch (error) {
      console.warn(error);

      showToast({
        data: {
          variant: 'error',
          title: getTranslation('common.toast.error.title'),
          text: getTranslation(
            'figma_lesson_page.main_settings.general_section.translate_error',
          ),
        },
      });
    } finally {
      setTranslateLoading(false);
    }
  };

  return (
    <GeneralSection
      {...restProps}
      namePrefix={namePrefix}
      translateLoading={translateLoading}
      activeLocale={activeLocale}
      onTranslateClick={handleTranslateClick}
    />
  );
};

export default GeneralSectionContainer;
